import { Component, Input, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateRangePickerComponent, DateRange } from '@shared/components/date-range-picker/date-range-picker.component';

@Component({
  selector: 'app-dcf-common-calendar',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DateRangePickerComponent],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DcfCommonCalendarComponent), multi: true }],
  template: `
    <div>
      <label *ngIf="fieldSchema?.label" class="block text-sm font-medium text-gray-700 mb-1">
        {{ fieldSchema.label }}
      </label>
      <div class="relative">
        <input
          #triggerInput
          type="text"
          [value]="formattedValue"
          [placeholder]="getPlaceholder()"
          [disabled]="disabled"
          (click)="openCalendar()"
          readonly
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer bg-white"
          [class.opacity-50]="disabled"
        />
        <app-date-range-picker
          [show]="isCalendarOpen"
          [triggerElement]="triggerInput"
          [inline]="false"
          [singleDateMode]="dateConfig?.singleDateMode ?? true"
          [dateSelectionMode]="dateConfig?.dateSelectionMode ?? 'all'"
          [includeToday]="dateConfig?.includeToday ?? true"
          (showChange)="onCalendarShowChange($event)"
          (dateRangeSelected)="onDateSelected($event)">
        </app-date-range-picker>
      </div>
    </div>
  `,
  styles: [`
    .w-full { width: 100%; }
    .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
    .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .border { border-width: 1px; }
    .border-gray-300 { border-color: #d1d5db; }
    .rounded-md { border-radius: 0.375rem; }
    .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
    .cursor-pointer { cursor: pointer; }
    .opacity-50 { opacity: 0.5; }
    .bg-white { background-color: white; }
    .relative { position: relative; }
    input:focus {
      outline: none;
      box-shadow: 0 0 0 1px #3b82f6;
      border-color: #3b82f6;
    }
  `]
})
export class DcfCommonCalendarComponent implements ControlValueAccessor {
  @Input() fieldSchema: any;
  @Input() formControl!: FormControl;

  @ViewChild('triggerInput') triggerInput!: ElementRef<HTMLInputElement>;

  value: any = null;
  disabled: boolean = false;
  isCalendarOpen: boolean = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  get dateConfig() {
    return this.fieldSchema?.dateConfig;
  }

  get formattedValue(): string {
    if (!this.value) return '';

    if (this.dateConfig?.singleDateMode) {
      // Single date mode
      if (this.value instanceof Date) {
        return this.formatDate(this.value);
      }
    } else {
      // Date range mode
      if (this.value?.startDate && this.value?.endDate) {
        return `${this.formatDate(this.value.startDate)} - ${this.formatDate(this.value.endDate)}`;
      }
    }

    return '';
  }

  getPlaceholder(): string {
    const customPlaceholder = this.fieldSchema?.placeholder;
    if (customPlaceholder) return customPlaceholder;

    return this.dateConfig?.singleDateMode ? 'Select date' : 'Select date range';
  }

  formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  openCalendar(): void {
    if (!this.disabled) {
      this.isCalendarOpen = true;
    }
  }

  onCalendarShowChange(show: boolean): void {
    this.isCalendarOpen = show;
  }

  onDateSelected(dateRange: DateRange): void {
    // For single date mode, use startDate; for range mode, use the full range
    const value = this.dateConfig?.singleDateMode ? dateRange.startDate : dateRange;
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.isCalendarOpen = false;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
